import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { LoggerService } from '@app/core/infrastructure';
import { UserSettingsSchema, UserSettingsUpdateInput } from './user-settings-api.models';

/**
 * Pure API service for user settings HTTP operations
 * No state management - just HTTP calls
 */
@Injectable({
  providedIn: 'root'
})
export class UserSettingsApiService {
  private readonly logger = inject(LoggerService).scope('UserSettingsApiService');

  private apiBaseUrl = `${environment.API_BASE_URL}/protected/users`;

  constructor(private http: HttpClient) {
    this.logger.debug('Initialized');
  }

  /**
   * GET /me/settings - Retrieve user settings
   * Auto-creates record with defaults if not exists
   */
  public getSettings(): Observable<UserSettingsSchema> {
    return this.http.get<UserSettingsSchema>(`${this.apiBaseUrl}/me/settings`).pipe(
      catchError(error => {
        this.logger.error('Error fetching user settings:', error);
        return throwError(() => new Error('Failed to fetch user settings'));
      })
    );
  }

  /**
   * PUT /me/settings - Update user settings
   * Replaces provided fields only (omitted fields unchanged)
   */
  public updateSettings(data: UserSettingsUpdateInput): Observable<UserSettingsSchema> {
    return this.http.put<UserSettingsSchema>(`${this.apiBaseUrl}/me/settings`, data).pipe(
      catchError(error => {
        this.logger.error('Error updating user settings:', error);
        return throwError(() => error);  // Re-throw original error for status code handling (e.g. 422)
      })
    );
  }
}
